import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  AndamentoProcesso,
  DocumentoProcesso,
  MensagemChatItem,
  PendenciaProcesso,
  CriarSolicitacaoPayload,
  SolicitacaoAlteracao,
} from '@/features/arbitral-detalhes';
import { getErrorMessage } from '@/shared/utils/error';

import type { AutosDoAdvogado } from '../types';
import { advogadoAutosService } from './servicesAutos';

interface RespostaPendenciaInput {
  processoId: string;
  pendenciaId: string;
  acao: 'resolver' | 'reenviar';
  resposta: string;
  arquivos?: File[];
}

export const carregarAutosAdvogado = createAsyncThunk<
  AutosDoAdvogado,
  string,
  { rejectValue: string }
>('advogadoAutos/carregar', async (processoId, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.obterAutos(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarAndamentosAdvogado = createAsyncThunk<
  AndamentoProcesso[],
  string,
  { rejectValue: string }
>('advogadoAutos/carregarAndamentos', async (processoId, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.listarAndamentos(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const registrarAndamentoAdvogado = createAsyncThunk<
  AndamentoProcesso,
  { processoId: string; descricao: string; arquivos: File[] },
  { rejectValue: string }
>('advogadoAutos/registrarAndamento', async ({ processoId, ...dados }, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.criarAndamento(processoId, dados);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const criarSolicitacaoAdvogado = createAsyncThunk<
  SolicitacaoAlteracao,
  { processoId: string; payload: CriarSolicitacaoPayload },
  { rejectValue: string }
>('advogadoAutos/criarSolicitacao', async ({ processoId, payload }, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.criarSolicitacao(processoId, payload);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarDocumentosAdvogado = createAsyncThunk<
  DocumentoProcesso[],
  string,
  { rejectValue: string }
>('advogadoAutos/carregarDocumentos', async (processoId, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.listarDocumentos(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarPendenciasAdvogado = createAsyncThunk<
  PendenciaProcesso[],
  string,
  { rejectValue: string }
>('advogadoAutos/carregarPendencias', async (processoId, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.listarPendencias(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const responderPendenciaAdvogado = createAsyncThunk<
  PendenciaProcesso[] | null,
  RespostaPendenciaInput,
  { rejectValue: string }
>(
  'advogadoAutos/responderPendencia',
  async ({ processoId, pendenciaId, acao, resposta, arquivos }, { rejectWithValue }) => {
    try {
      await advogadoAutosService.responderPendencia(
        processoId,
        pendenciaId,
        acao,
        resposta,
        arquivos,
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }

    try {
      return await advogadoAutosService.listarPendencias(processoId);
    } catch {
      return null;
    }
  },
);

export const carregarChatAdvogado = createAsyncThunk<
  MensagemChatItem[],
  string,
  { rejectValue: string }
>('advogadoAutos/carregarChat', async (processoId, { rejectWithValue }) => {
  try {
    const { mensagens } = await advogadoAutosService.listarMensagensChat(processoId);
    return mensagens;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const enviarMensagemAdvogado = createAsyncThunk<
  MensagemChatItem,
  { processoId: string; texto: string; arquivos: File[] },
  { rejectValue: string }
>('advogadoAutos/enviarMensagem', async ({ processoId, texto, arquivos }, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.enviarMensagemChat(processoId, texto, arquivos);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const marcarMensagemLidaAdvogado = createAsyncThunk<
  string,
  { processoId: string; mensagemId: string },
  { rejectValue: string }
>('advogadoAutos/marcarLida', async ({ processoId, mensagemId }, { rejectWithValue }) => {
  try {
    await advogadoAutosService.marcarMensagemLida(processoId, mensagemId);
    return mensagemId;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarSolicitacoesAdvogado = createAsyncThunk<
  SolicitacaoAlteracao[],
  string,
  { rejectValue: string }
>('advogadoAutos/carregarSolicitacoes', async (processoId, { rejectWithValue }) => {
  try {
    return await advogadoAutosService.listarSolicitacoes(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});
